import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Button from '../atoms/Button';
import { FaTimes, FaInfo, FaClock, FaBell, FaPalette, FaKeyboard, FaLanguage, FaMoon } from 'react-icons/fa';

interface AboutPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutPanel: React.FC<AboutPanelProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-gray-800 shadow-lg z-[80] overflow-y-auto"
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold flex items-center">
            <FaInfo className="mr-2" /> {t('aboutTitle')}
          </h2>
          <Button 
            variant="outline" 
            onClick={onClose} 
            aria-label={t('close')}
            icon={<FaTimes />}
          />
        </div>
        
        {/* 网站简介 */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold mb-3">{t('aboutIntroTitle')}</h3>
          <p className="mb-3">{t('aboutIntroText')}</p>
        </section>
        
        {/* 主要功能 */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold mb-3">{t('aboutFeaturesTitle')}</h3>
          
          <div className="space-y-4">
            {/* 计时器和秒表 */}
            <div className="feature-item p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-medium flex items-center mb-2">
                <FaClock className="mr-2 text-blue-600 dark:text-blue-400" />
                {t('aboutFeatureTimersTitle')}
              </h4>
              <p className="text-sm">{t('aboutFeatureTimersText')}</p>
            </div>
            
            {/* 通知功能 */}
            <div className="feature-item p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <h4 className="font-medium flex items-center mb-2">
                <FaBell className="mr-2 text-purple-600 dark:text-purple-400" />
                {t('aboutFeatureNotificationsTitle')}
              </h4>
              <p className="text-sm">{t('aboutFeatureNotificationsText')}</p>
            </div>
            
            {/* 视觉效果 */}
            <div className="feature-item p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <h4 className="font-medium flex items-center mb-2">
                <FaPalette className="mr-2 text-green-600 dark:text-green-400" />
                {t('aboutFeatureEffectsTitle')}
              </h4>
              <p className="text-sm">{t('aboutFeatureEffectsText')}</p>
            </div>
            
            {/* 快捷方式 */}
            <div className="feature-item p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <h4 className="font-medium flex items-center mb-2">
                <FaKeyboard className="mr-2 text-yellow-600 dark:text-yellow-400" />
                {t('aboutFeatureShortcutsTitle')}
              </h4>
              <p className="text-sm">{t('aboutFeatureShortcutsText')}</p>
            </div>
            
            {/* 多语言支持 */}
            <div className="feature-item p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <h4 className="font-medium flex items-center mb-2">
                <FaLanguage className="mr-2 text-red-600 dark:text-red-400" />
                {t('aboutFeatureLanguagesTitle')}
              </h4>
              <p className="text-sm">{t('aboutFeatureLanguagesText')}</p>
            </div>
            
            {/* 深色模式 */}
            <div className="feature-item p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium flex items-center mb-2">
                <FaMoon className="mr-2 text-gray-600 dark:text-gray-400" />
                {t('aboutFeatureDarkModeTitle')}
              </h4>
              <p className="text-sm">{t('aboutFeatureDarkModeText')}</p>
            </div>
          </div>
        </section>
        
        {/* 使用指南 */}
        <section className="mb-8">
          <h3 className="text-lg font-semibold mb-3">{t('aboutHowToUseTitle')}</h3>
          
          <div className="space-y-4">
            <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h4 className="font-medium mb-2">{t('aboutHowToCreateTitle')}</h4>
              <p className="text-sm">{t('aboutHowToCreateText')}</p>
            </div>
            
            <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h4 className="font-medium mb-2">{t('aboutHowToControlTitle')}</h4>
              <p className="text-sm">{t('aboutHowToControlText')}</p>
            </div>
            
            <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h4 className="font-medium mb-2">{t('aboutHowToCustomizeTitle')}</h4>
              <p className="text-sm">{t('aboutHowToCustomizeText')}</p>
            </div>
            
            <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h4 className="font-medium mb-2">{t('aboutHowToManageTitle')}</h4>
              <p className="text-sm">{t('aboutHowToManageText')}</p>
            </div>
          </div>
        </section>
        
        {/* 关于项目 */}
        <section>
          <h3 className="text-lg font-semibold mb-3">{t('aboutProjectTitle')}</h3>
          <p className="mb-2">{t('aboutProjectText')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('aboutVersionInfo')}</p>
        </section>
      </div>
    </motion.div>
  );
};

export default AboutPanel; 